import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LockIcon, 
  FileTextIcon,
  MessageCircleHeartIcon,
  Send,
  X,
  Bot
} from 'lucide-react';
import axios from 'axios';

const Footer = () => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const chatRef = useRef(null);
  const buttonRef = useRef(null);
  const messagesEndRef = useRef(null);
  // Set up OpenAI API key here or use env variables for security
  const openAiApiKey = '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';  // Replace with your OpenAI API Key
  
 

  const formatResponse = (text) => {
    if (text.includes('. **')) {
      const sections = text.split(/\d+\. \*\*/);
      return sections.filter(Boolean).map(section => {
        return section.replace(/\*\*/g, '').trim();
      });
    }
    return [text];
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    const userMessage = message;
    setMessage('');
    setChatHistory(prev => [...prev, { type: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await query(userMessage);
      const formattedSections = formatResponse(response || "I couldn't process that request.");
      
      setChatHistory(prev => [...prev, { 
        type: 'bot', 
        content: formattedSections
      }]);
    } catch (error) {
      console.error('Chat error:', error);
      setChatHistory(prev => [...prev, { 
        type: 'bot', 
        content: ["I'm having trouble connecting. Please try again."]
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  async function query(data) {
    try {
      const response = await axios.post(
        'https://api.openai.com/v1/chat/completions',
        {
          model: 'gpt-3.5-turbo',
          messages: [
            { role: 'system', content: 'You are a helpful health assistant.' },
            { role: 'user', content: data }
          ],
          max_tokens: 150
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${openAiApiKey}`
          }
        }
      );

      return response.data.choices[0].message.content;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  }

 

  useEffect(() => {
    if (isChatOpen && chatHistory.length === 0) {
      setChatHistory([{ 
        type: 'bot', 
        content: ["Hi there! 👋 How can I assist you with your health questions today?"]
      }]);
    }
  }, [isChatOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isChatOpen &&
        chatRef.current &&
        !chatRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsChatOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isChatOpen]);

  return (
    <motion.footer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-gray-800 text-white py-6 relative"
    >
      <div className="container mx-auto px-4 text-center">
        <p className="text-sm mb-2">© {new Date().getFullYear()} mastermyhealthai. All rights reserved.</p>
        <div className="flex flex-wrap justify-center items-center gap-4 text-sm">
          <motion.a
            href="#"
            whileHover={{ scale: 1.05 }}
            className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition"
          >
            <LockIcon size={16} />
            Privacy Policy
          </motion.a>
          <span className="hidden md:inline text-gray-500">|</span>
          <motion.a
            href="#"
            whileHover={{ scale: 1.05 }}
            className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition"
          >
            <FileTextIcon size={16} />
            Terms of Service
          </motion.a>
        </div>
      </div>

      <motion.button
        ref={buttonRef}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        className="fixed right-4 bottom-20 md:right-6 md:bottom-6 bg-blue-600 p-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors z-50"
        onClick={() => setIsChatOpen(!isChatOpen)}
      >
        <MessageCircleHeartIcon size={24} className="text-white" />
      </motion.button>

      <AnimatePresence>
        {isChatOpen && (
          <motion.div
            ref={chatRef}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed right-4 bottom-36 md:right-6 md:bottom-24 w-[calc(100%-2rem)] md:w-[400px] max-h-[600px] bg-white rounded-2xl shadow-2xl overflow-hidden z-40"
          >
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 flex items-center gap-3">
              <div className="bg-white/10 p-2 rounded-lg">
                <Bot size={20} className="text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-medium">Health Assistant</h3>
                <p className="text-blue-100 text-sm">Always here to help</p>
              </div>
              <button 
                onClick={() => setIsChatOpen(false)}
                className="text-white/80 hover:text-white hover:bg-white/10 p-2 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="h-[400px] overflow-y-auto p-4 bg-gray-50">
              <div className="space-y-4">
                {chatHistory.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] ${
                        msg.type === 'user'
                          ? 'bg-blue-600 text-white rounded-t-2xl rounded-l-2xl'
                          : 'bg-white text-gray-800 shadow-sm rounded-t-2xl rounded-r-2xl'
                      } p-4`}
                    >
                      {Array.isArray(msg.content) ? (
                        <div className="space-y-3">
                          {msg.content.map((section, idx) => (
                            <div key={idx} className="flex gap-2">
                              {msg.type === 'bot' && msg.content.length > 1 && (
                                <span className="font-bold min-w-[24px]">{idx + 1}.</span>
                              )}
                              <p className="flex-1 text-sm">{section}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm">{msg.content}</p>
                      )}
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-white shadow-sm rounded-2xl p-4 max-w-[80%]">
                      <div className="flex items-center gap-2 text-gray-500">
                        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" />
                        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>

            <form onSubmit={handleSendMessage} className="p-4 bg-white border-t">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-1 p-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 text-sm text-gray-800 placeholder-gray-400"
                />
                <button
                  type="submit"
                  disabled={isLoading}
                  className="bg-blue-600 text-white p-3 rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  <Send size={20} />
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.footer>
  );
};

export default Footer;
